import { Lock } from "lucide-react";
import { useRoleGranters } from "@/hooks/queries/use-toolbox-access";
import { joinNames } from "@/lib/toolbox/join-names";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Repos page's Grooming state (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1467, UX-PM-43; 03-evaluation.md section 8.6 Q7):
 *
 * - {@link GroomingSwitch} (Admin, frame 1e): a visible on/off switch in place
 *   of the silent turn-on when the first repository was saved. It calls
 *   `PATCH /grooming/projects/:project/enabled`; repositories stay set up
 *   either way.
 * - {@link RepoLockLine} (Project Manager, frame 1f): the page is read-only,
 *   so the switch becomes a sentence, and the lock line names the Admins to
 *   ask (`GET /toolbox/access/granters`).
 */

const GLASS_CARD = "glass-card flex items-center gap-3 rounded-[10px] px-4 py-3.5";

export interface GroomingSwitchProps {
  project: string;
  enabled: boolean;
  /** Why the switch cannot be used yet (the project is not set up), or undefined. */
  disabledReason?: string | undefined;
  pending: boolean;
  /** The plain reason the last change failed, or null. */
  error: string | null;
  onChange: (enabled: boolean) => void;
}

/** The Admin's Grooming on/off switch (36 x 20, on #22C55E, off #3F3F46). */
export function GroomingSwitch({
  project,
  enabled,
  disabledReason,
  pending,
  error,
  onChange,
}: GroomingSwitchProps) {
  const disabled = pending || disabledReason !== undefined;
  return (
    <div className="flex flex-col gap-2">
      <div className={GLASS_CARD} data-testid="grooming-switch-card">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`Grooming for ${project}`}
          title={disabledReason}
          disabled={disabled}
          className={cn(
            "relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            enabled ? "bg-[#22C55E]" : "bg-[#3F3F46]"
          )}
          onClick={() => {
            onChange(!enabled);
          }}
          data-testid="grooming-switch"
        >
          <span
            aria-hidden="true"
            className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-[left]",
              enabled ? "left-[18px]" : "left-0.5"
            )}
          />
        </button>
        <p
          className={cn(TOOLBOX_TEXT.content, "min-w-0 flex-1 leading-relaxed")}
          data-testid="grooming-switch-text"
        >
          <b className="font-semibold">
            Grooming for {project} is {enabled ? "on" : "off"}.
          </b>{" "}
          <span className="text-muted-foreground">
            {disabledReason ??
              (enabled
                ? "Turning it off hides this project from the Backlog and Session. Repositories stay set up."
                : "Repositories stay set up. Turn it on when the repositories are right.")}
          </span>
        </p>
      </div>
      {error && (
        <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert">
          Grooming was not turned {enabled ? "off" : "on"}: {error}
        </p>
      )}
    </div>
  );
}

export interface RepoLockLineProps {
  project: string;
  /** Grooming's state; undefined while the project is not set up. */
  enabled: boolean | undefined;
}

/** The Project Manager's read-only line: who sets the repositories up, and Grooming's state as a sentence. */
export function RepoLockLine({ project, enabled }: RepoLockLineProps) {
  const granters = useRoleGranters(project);
  const admins = joinNames((granters.data?.admins ?? []).map((admin) => admin.name), "or", "an Admin");
  const state =
    enabled === undefined
      ? `${project} is not set up for grooming yet.`
      : `Grooming for ${project} is ${enabled ? "on" : "off"}.`;
  return (
    <div className={GLASS_CARD} data-testid="repo-lock-line">
      <Lock className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint)} aria-hidden="true" />
      <p className={cn(TOOLBOX_TEXT.content, "min-w-0 flex-1 leading-relaxed")}>
        <b className="font-semibold">Set up by an Admin.</b>{" "}
        <span className="text-muted-foreground">
          To add or change a repository, ask {admins}. {state}
        </span>
      </p>
    </div>
  );
}
